import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { PublicStory } from "@/lib/publicStories";

export default function StoryGrid({ stories }: { stories: PublicStory[] }) {
  const [active, setActive] = useState("All");

  const categories = useMemo(() => {
    const unique = Array.from(new Set(stories.map((story) => story.category).filter(Boolean)));
    return ["All", ...unique.sort((a, b) => a.localeCompare(b))];
  }, [stories]);

  const filtered = useMemo(
    () => (active === "All" ? stories : stories.filter((story) => story.category === active)),
    [active, stories],
  );

  return (
    <section id="journal" className="sun-glaze bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-12">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              The Journal
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
            More Stories From the Soil
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Notes from the field — farmer journeys, seasonal diaries, guides and rural traditions,
            written slowly and told honestly.
          </p>
        </Reveal>

        <Reveal className="mb-10">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => {
              const isActive = cat === active;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActive(cat)}
                  className={`text-sm font-medium px-4 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                    isActive
                      ? "bg-secondary-500 border-secondary-500 text-background-50"
                      : "bg-background-50 border-background-300 text-foreground-700 hover:border-secondary-300 hover:text-secondary-700"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {filtered.map((post, index) => (
            <Reveal key={post.id} delay={(index % 3) * 80}>
              <Link
                to={`/stories/${post.slug}`}
                className="group h-full bg-background-50 rounded-lg border border-background-200 overflow-hidden flex flex-col hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="relative w-full h-48 overflow-hidden bg-background-200">
                  <img
                    src={post.cover}
                    width={1200}
                    height={800}
                    alt={`${post.title} — cover image for this Rythugaru farmer story from ${post.village}`}
                    title={`${post.title} - Rythugaru journal`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                    decoding="async"
                  />
                  <span className="absolute top-4 left-4 bg-background-50/95 text-foreground-800 text-[11px] font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                    {post.category}
                  </span>
                  {post.source === "blog" ? (
                    <span className="absolute top-4 right-4 bg-accent-500 text-background-50 text-[10px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap uppercase tracking-wider">
                      Blog
                    </span>
                  ) : null}
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <p className="text-xs text-foreground-500">
                    {post.date} · {post.readTime}
                  </p>
                  <h3 className="font-heading text-xl text-foreground-950 font-semibold leading-snug mt-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mt-3 flex-1">
                    {post.excerpt}
                  </p>
                  <div className="mt-5 pt-4 border-t border-background-200 flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                        <i className="ri-user-voice-line"></i>
                      </span>
                      <span className="text-xs font-medium text-foreground-700 truncate">
                        {post.author} · {post.village}
                      </span>
                    </span>
                    <i className="ri-arrow-right-line text-primary-700 transition-transform group-hover:translate-x-1"></i>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}